import React, { useRef } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Transition, TransitionStatus } from "react-transition-group";

import { IPickerTab, PickerItem } from "./Picker.types";
import { PickerTabButton } from "./PickerTabButton";

const timing = 333;

const TabList = styled.ul<{ state: TransitionStatus }>(
    css`
        margin: 0;
        padding: 0;
        display: flex;
        list-style: none;
        column-gap: ${4 / 16}rem;
        padding-inline: ${14 / 16}rem;
        overflow-x: auto;
        overflow-y: hidden;

        scrollbar-width: none;
        ::-webkit-scrollbar {
            display: none;
        }

        transition:
            max-height ${timing}ms ease-out,
            opacity ${timing}ms ease-out;
    `,
    props => {
        switch (props.state) {
            case "entering":
                return {
                    opacity: 1,
                    maxHeight: `${42 / 16}rem`,
                    overflowY: "hidden",
                };
            case "entered":
                return {
                    maxHeight: `${42 / 16}rem`,
                    opacity: 1,
                };
            case "exiting":
                return {
                    maxHeight: "0",
                    overflowY: "hidden",
                    opacity: 0,
                };
            case "exited":
                return { maxHeight: "0", opacity: 0 };
        }
    },
);

interface IPickerTabsProps<TTab extends string, TGroup extends string> {
    selectedTab?: TTab;
    onSelectTab?: (id: TTab) => void;
    isHidden?: boolean;
    items: PickerItem<TTab, TGroup>[];
    tabs: IPickerTab<TTab>[];
}

export function PickerTabs<TTab extends string, TGroup extends string>(
    props: IPickerTabsProps<TTab, TGroup>,
) {
    const {
        items,
        isHidden = false,
        onSelectTab,
        selectedTab,
        tabs = [],
    } = props;

    const nodeRef = useRef<HTMLUListElement | null>(null);

    const tabElements = tabs.map(({ id, label, icon }) => {
        const disabled = !items.some(it => it.tab === id);
        return (
            <PickerTabButton
                key={id}
                id={id}
                isDisabled={disabled}
                isSelected={selectedTab === id}
                onSelect={onSelectTab}
                data-testid={`picker-tab-${id}`}
            >
                {icon}
                {label}
            </PickerTabButton>
        );
    });

    return (
        <Transition
            nodeRef={nodeRef}
            in={!isHidden}
            timeout={timing}
            mountOnEnter={true}
            unmountOnExit={true}
        >
            {state => (
                <TabList ref={nodeRef} state={state}>
                    {tabElements}
                </TabList>
            )}
        </Transition>
    );
}
